import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Megaphone, Truck, FileText, PackageX, Trash2, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApp } from "@/context/AppContext";
import { isLow, vals, fmtDate } from "@/lib/format";
import { NOTIF_KINDS, addNotif, clearNotifs, markAllRead, useNotifs } from "@/lib/notifications";

const ICONS = { admin: Megaphone, wholesale: Truck, billing: FileText, stock: PackageX };
const TONES = { admin: "bg-[#F28C28]/20 text-[#B85E05]", wholesale: "bg-[#5CC8E8]/20 text-[#0E6F8A]", billing: "bg-[#0B2A5B]/10 text-[#0B2A5B]", stock: "bg-[#C7353B]/15 text-[#C7353B]" };

// Watches products and records a stock alert the first time a product goes low (per product/day)
function useStockWatch() {
  const { view, t } = useApp();
  const first = useRef(true);
  useEffect(() => {
    const low = vals(view.products).filter((p) => !p.deleted && isLow(p, view.shop?.low_stock_default));
    if (first.current) { first.current = false; if (low.length) addNotif("stock", t("notif_stock_summary", { n: low.length }), { key: `stock:sum:${new Date().toDateString()}`, to: "/inventory" }); return; }
    low.forEach((p) => addNotif("stock", t("notif_stock_item", { name: p.name, n: p.stock || 0 }), { key: `stock:${p.id}:${new Date().toDateString()}`, to: "/inventory" }));
  }, [view.products, view.shop?.low_stock_default, t]);
}

export function NotificationBell({ compact }) {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const list = useNotifs();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("all");
  useStockWatch();
  const unread = list.filter((n) => !n.read).length;
  const shown = list.filter((n) => kind === "all" || n.kind === kind);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) markAllRead(); }}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={t("notif_title")} title={t("notif_title")} data-testid="notif-bell" className={`relative shrink-0 rounded hover:bg-white/15 grid place-items-center transition-colors ${compact ? "h-6 w-6" : "w-10 h-10"}`}>
          <Bell className={compact ? "w-3.5 h-3.5" : "w-5 h-5"} />
          {unread > 0 && <span className="absolute -top-0.5 -end-0.5 min-w-4 h-4 px-1 rounded-full bg-[#F28C28] text-[#1A1A1A] text-[10px] font-black grid place-items-center" data-testid="notif-badge">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 overflow-hidden rounded-2xl border-border" data-testid="notif-panel">
        <div className="px-4 py-3 bg-[#0B2A5B] text-white flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#F28C28]" /><span className="font-heading flex-1">{t("notif_title")}</span>
          <button type="button" onClick={markAllRead} title={t("notif_mark_read")} className="h-7 w-7 rounded hover:bg-white/15 grid place-items-center" data-testid="notif-mark-read"><CheckCheck className="w-4 h-4" /></button>
          <button type="button" onClick={clearNotifs} title={t("notif_clear")} className="h-7 w-7 rounded hover:bg-white/15 grid place-items-center" data-testid="notif-clear"><Trash2 className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1 p-2 border-b border-border bg-secondary/50 overflow-x-auto no-scrollbar">
          {["all", ...NOTIF_KINDS].map((k) => <button key={k} type="button" onClick={() => setKind(k)} className={`px-2.5 h-7 rounded-full text-xs font-bold whitespace-nowrap ${kind === k ? "bg-[#0B2A5B] text-white" : "bg-white border border-border"}`} data-testid={`notif-filter-${k}`}>{t(`notif_k_${k}`)}</button>)}
        </div>
        <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border" data-testid="notif-list">
          {!shown.length && <li className="p-6 text-center text-sm text-muted-foreground" data-testid="notif-empty">{t("notif_empty")}</li>}
          {shown.map((n) => { const I = ICONS[n.kind] || Bell; return (
            <li key={n.id} className={`flex gap-3 px-4 py-3 ${n.read ? "" : "bg-[#F28C28]/5"} ${n.to ? "cursor-pointer hover:bg-secondary" : ""}`} onClick={() => { if (n.to) { setOpen(false); markAllRead(); nav(n.to); } }} data-testid={`notif-item-${n.kind}`}>
              <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${TONES[n.kind]}`}><I className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm leading-snug whitespace-pre-line break-words line-clamp-4" dir="auto">{n.text}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{t(`notif_k_${n.kind}`)} · {fmtDate(n.at, lang)}</div>
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-[#F28C28] mt-2 shrink-0" />}
            </li>); })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
